import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as TestModule from 'modules/test';
import * as AuthModule from 'modules/auth';
import { PERMISSIONS } from 'modules/auth/permissions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { testPhase, type TestPhase } from 'components/workspace/lifecycle';
import { computeTestReadiness, type TestReadiness } from 'pages/TestConfig/testReadiness';

export interface TestLifecycle {
  testId: string;
  title: string;
  /** The API status, lower-cased ('draft', 'active', 'published', 'finished', 'archived'). */
  status: string;
  phase: TestPhase;
  readiness: TestReadiness;
  checksRemaining: number;
  activating: boolean;
  finishing: boolean;
  /** Only the author may delete (the API answers 403 to anyone else). */
  canDelete: boolean;
  /** Opens the activate (or, for a finished test, reactivate) confirmation. */
  requestActivate: () => void;
  finish: () => void;
  requestDelete: () => void;
}

const TestLifecycleContext = createContext<TestLifecycle | null>(null);

export function useTestLifecycle(): TestLifecycle {
  const value = useContext(TestLifecycleContext);
  if (!value) throw new Error('useTestLifecycle must be used inside TestLifecycleProvider');
  return value;
}

/**
 * The test's lifecycle actions for the whole workspace: the desktop header, the
 * phone menu and the phone Activate bar all trigger the same mutations and share
 * one set of confirmation dialogs.
 */
export function TestLifecycleProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { testId = '' } = useParams();
  const navigate = useNavigate();
  const {
    state: { permissions, user },
  } = AuthModule.Context.useAuthContext();

  const { item } = TestModule.Hooks.useSingle({ id: testId, enabled: Boolean(testId) });
  const activateTest = TestModule.Hooks.useActivate();
  const finishTest = TestModule.Hooks.useFinish();
  const deleteTest = TestModule.Hooks.useDelete();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const test = item as { title?: string; status?: string; author?: { id?: string } } | undefined;
  const status = (test?.status || 'draft').toLowerCase();
  const phase = testPhase(status);
  const readiness = useMemo(() => computeTestReadiness(item), [item]);
  const authorId = test?.author?.id;
  const canDelete = permissions.includes(PERMISSIONS.TESTS_DELETE) && Boolean(authorId) && authorId === user?.id;

  const handleActivate = useCallback(async () => {
    if (!testId) return;
    try {
      await activateTest.mutateAsync({ id: testId });
      setConfirmOpen(false);
    } catch {
      /* toast handled by hook */
    }
  }, [testId, activateTest]);

  const finish = useCallback(async () => {
    if (!testId) return;
    try {
      await finishTest.mutateAsync({ id: testId });
    } catch {
      /* toast handled by hook */
    }
  }, [testId, finishTest]);

  // Deleting the test destroys the workspace we're standing in — leave for the
  // list before the dialog closes, so no child route refetches a dead id.
  const handleDelete = useCallback(async () => {
    if (!testId) return;
    try {
      await deleteTest.mutateAsync({ id: testId });
      setDeleteOpen(false);
      navigate('/app/tests');
    } catch {
      /* toast handled by hook */
    }
  }, [testId, deleteTest, navigate]);

  const title = test?.title || t('new_test');
  const value = useMemo<TestLifecycle>(
    () => ({
      testId,
      title,
      status,
      phase,
      readiness,
      checksRemaining: readiness.total - readiness.doneCount,
      activating: activateTest.isPending,
      finishing: finishTest.isPending,
      canDelete,
      requestActivate: () => setConfirmOpen(true),
      finish,
      requestDelete: () => setDeleteOpen(true),
    }),
    [testId, title, status, phase, readiness, activateTest.isPending, finishTest.isPending, canDelete, finish],
  );

  const reactivating = phase === 'finished';

  return (
    <TestLifecycleContext.Provider value={value}>
      {children}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{reactivating ? t('reactivate_test') : t('activate_test')}</AlertDialogTitle>
            <AlertDialogDescription>
              {reactivating ? t('reactivate_test_confirm') : t('activate_test_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={activateTest.isPending}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleActivate();
              }}
              disabled={activateTest.isPending}
            >
              {reactivating ? t('reactivate_test') : t('activate_test')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Moves the test to the trash; the daily purge removes it after the retention period. */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('delete_assessment')}
        description={t('trash.test_delete_description')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={deleteTest.isPending}
        onConfirm={handleDelete}
      />
    </TestLifecycleContext.Provider>
  );
}

export default TestLifecycleProvider;
